import type { ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useBackState } from '@/lib/back-nav';

import { usePersonRework, type PersonReworkFilter } from '../use-rework';
import { ReworkEventList } from './ReworkEventList';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

// "Reprocesos de {persona}" (spec 2026-10-14 §5 D2): las devoluciones atribuidas a una
// persona (o sin atribuir) con el rango y los filtros de la tarjeta del Estudio de
// tiempos, paginadas.
// Cada código abre la ficha con regreso al Estudio de tiempos. Sin página nueva.

export interface PersonRef {
  /** Id de la persona, o null para "Sin atribuir". */
  userId: string | null;
  name: string;
}

export function PersonReworkModal({
  person,
  filter,
  companyId,
  onClose,
}: {
  person: PersonRef;
  /** Empresa activa (la misma de la tarjeta que abre el diálogo). */
  companyId: string;
  filter: PersonReworkFilter;
  onClose: () => void;
}): ReactElement {
  const back = useBackState('Estudio de tiempos');
  const query = usePersonRework(person.userId ?? 'none', filter, { companyId, enabled: true });
  const events = query.data?.pages.flatMap((p) => p.data) ?? [];

  let body: ReactElement;
  // Un fallo al pedir la página siguiente no borra lo ya cargado: se avisa abajo.
  if (query.isError && events.length === 0) {
    body = (
      <div role="alert">
        <p className="state-msg">
          No se pudieron cargar los reprocesos. {errorAdvice(query.error)}
        </p>
        <button type="button" className="btn mut sm" onClick={() => void query.refetch()}>
          Reintentar
        </button>
      </div>
    );
  } else if (query.isLoading) {
    body = (
      <div aria-busy="true" aria-label="Cargando reprocesos">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={skeletonLine({ width: 90, space: 'y' })} />
        ))}
      </div>
    );
  } else if (events.length === 0) {
    body = <p className="soft">No hubo reprocesos en el rango.</p>;
  } else {
    body = (
      <>
        <ReworkEventList events={events} backState={back} />
        {query.isFetchNextPageError ? (
          <div role="alert">
            <p className="state-msg">
              No se pudieron cargar más reprocesos. {errorAdvice(query.error)}
            </p>
            <button
              type="button"
              className="btn mut sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Reintentar
            </button>
          </div>
        ) : (
          query.hasNextPage && (
            <button
              type="button"
              className="btn mut sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          )
        )}
      </>
    );
  }

  return (
    <ModalShell
      open
      wide
      title={person.userId === null ? 'Reprocesos sin atribuir' : `Reprocesos de ${person.name}`}
      onClose={onClose}
      footer={
        <button type="button" className="btn mut sm" onClick={onClose}>
          Cerrar
        </button>
      }
    >
      {body}
    </ModalShell>
  );
}
